import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { formatCents, dollarsToCents } from '../lib/money';

export default function AdminProducts() {
  const [planConfig, setPlanConfig] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [config, setConfig] = useState(null);
  const [couponConfig, setCouponConfig] = useState([]);
  const [promoCatalog, setPromoCatalog] = useState([]);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [newEntry, setNewEntry] = useState({
    stripePriceId: '',
    displayName: '',
    category: 'plan',
    sortOrder: 0,
    featured: true,
    linkedFeePriceId: '',
    serviceTag: '',
  });
  const [newCoupon, setNewCoupon] = useState({ stripePromotionCodeId: '', displayLabel: '', sortOrder: 0 });
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [linkingHcp, setLinkingHcp] = useState(false);
  const [hcpLinkResult, setHcpLinkResult] = useState(null);
  const [runningHcpSmokeTest, setRunningHcpSmokeTest] = useState(false);
  const [hcpSmokeTestResult, setHcpSmokeTestResult] = useState(null);
  const [repriceEntryId, setRepriceEntryId] = useState('');
  const [repriceNewAmount, setRepriceNewAmount] = useState('');
  const [repricePreview, setRepricePreview] = useState(null);
  const [loadingRepricePreview, setLoadingRepricePreview] = useState(false);
  const [repriceError, setRepriceError] = useState(null);
  const [submittingReprice, setSubmittingReprice] = useState(false);
  const [repriceResult, setRepriceResult] = useState(null);

  async function load() {
    try {
      const [pc, cat, cfg, cc, promoCat] = await Promise.all([
        api.get('/admin/plan-config'),
        api.get('/products/stripe-catalog'),
        api.get('/admin/config'),
        api.get('/admin/coupon-config'),
        api.get('/products/stripe-promo-catalog'),
      ]);
      setPlanConfig(pc.planConfig);
      setCatalog(cat.prices);
      setConfig(cfg);
      setCouponConfig(cc.couponConfig);
      setPromoCatalog(promoCat.promotionCodes);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function refresh() {
    setRefreshing(true);
    try {
      await load();
    } finally {
      setRefreshing(false);
    }
  }

  /**
   * Imports every Stripe subscription into Current Plans, including ones
   * never created through this tool (old self-serve checkout, added
   * directly in Stripe). Safe to run more than once -- already-imported
   * rows just get their status/pricing refreshed, never duplicated.
   * Subscriptions created going forward sync automatically via webhook;
   * this is only needed for the backlog that predates that, or as a
   * manual "force a refresh now" if something seems out of sync.
   */
  async function syncSubscriptions() {
    setSyncing(true);
    setSyncResult(null);
    setError(null);
    try {
      const result = await api.post('/admin/sync-subscriptions', {});
      setSyncResult(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  }

  /**
   * WS5: matches every unlinked order to its HouseCallPro customer record
   * (local customers table first, live HCP search only as a fallback) and
   * populates pest_orders.customer_id. Read-only against HCP -- never
   * creates anything there. Safe to run more than once; already-linked
   * orders are skipped.
   */
  async function linkHcpCustomers() {
    setLinkingHcp(true);
    setHcpLinkResult(null);
    setError(null);
    try {
      const result = await api.post('/admin/link-hcp-customers', {});
      setHcpLinkResult(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLinkingHcp(false);
    }
  }

  /**
   * Diagnostic only -- calls every HCP write endpoint directly (create
   * customer, create job, line item, add a second address, note) against
   * the real HCP account, completely separate from Stripe/orders. Lets us
   * verify field-name assumptions without a real signup or a real card.
   * Creates real, obviously-tagged test records in HCP -- see the result's
   * links to go delete them afterward.
   */
  async function runHcpSmokeTest() {
    if (
      !window.confirm(
        'This creates a REAL test customer and job in your live HouseCallPro account (there is no HCP sandbox). ' +
          'It\'s clearly tagged "SAFE-TO-DELETE" for cleanup, but it will be visible to anyone in HCP. Continue?'
      )
    ) {
      return;
    }
    setRunningHcpSmokeTest(true);
    setHcpSmokeTestResult(null);
    setError(null);
    try {
      const result = await api.post('/admin/hcp-smoke-test', {});
      setHcpSmokeTestResult(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setRunningHcpSmokeTest(false);
    }
  }

  async function toggleActive(entry) {
    try {
      await api.patch(`/admin/plan-config/${entry.id}`, { active: !entry.active });
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function removeEntry(entry) {
    if (!window.confirm(`Remove "${entry.display_name}" from the CSR allowlist? This does not affect existing orders.`)) {
      return;
    }
    try {
      await api.delete(`/admin/plan-config/${entry.id}`);
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function toggleFeatured(entry) {
    try {
      await api.patch(`/admin/plan-config/${entry.id}`, { featured: !entry.featured });
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function setLinkedFee(entry, priceId) {
    try {
      await api.patch(`/admin/plan-config/${entry.id}`, { linkedFeePriceId: priceId || null });
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  /**
   * Inline "edit on blur" for the two free-text fields in the product
   * table (display name, HCP tag) -- no dedicated edit mode/button, just
   * save if the value actually changed when the field loses focus. Empty
   * is a valid save for the tag (clears it, matching the PATCH route's
   * hasServiceTag handling) but not for display name, which is NOT NULL.
   */
  async function saveDisplayName(entry, value) {
    const trimmed = value.trim();
    if (!trimmed || trimmed === entry.display_name) return;
    try {
      await api.patch(`/admin/plan-config/${entry.id}`, { displayName: trimmed });
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function saveServiceTag(entry, value) {
    const trimmed = value.trim();
    if (trimmed === (entry.service_tag || '')) return;
    try {
      await api.patch(`/admin/plan-config/${entry.id}`, { serviceTag: trimmed || null });
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  /**
   * On-demand backfill/refresh of one row's cached line item description --
   * the automatic cache only ever fires once, at the moment a price is
   * first added (see saveDisplayName's neighbor addEntry), so this is the
   * only way to populate it for a row that predates that, or to force an
   * early refresh rather than waiting for the daily staleness check.
   */
  const [fetchingDescriptionId, setFetchingDescriptionId] = useState(null);
  async function fetchDescription(entry) {
    setFetchingDescriptionId(entry.id);
    try {
      await api.post(`/admin/plan-config/${entry.id}/fetch-description`, {});
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setFetchingDescriptionId(null);
    }
  }

  async function addEntry(e) {
    e.preventDefault();
    const price = catalog.find((p) => p.id === newEntry.stripePriceId);
    if (!price) return;
    try {
      await api.post('/admin/plan-config', {
        stripeProductId: price.productId,
        stripePriceId: price.id,
        displayName: newEntry.displayName || price.productName,
        category: newEntry.category,
        sortOrder: Number(newEntry.sortOrder) || 0,
        featured: newEntry.featured,
        linkedFeePriceId: newEntry.linkedFeePriceId || null,
        serviceTag: newEntry.serviceTag || null,
      });
      setNewEntry({ stripePriceId: '', displayName: '', category: 'plan', sortOrder: 0, featured: true, linkedFeePriceId: '', serviceTag: '' });
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  /**
   * Item 5: fetches the current live price plus how many non-canceled
   * subscriptions carry it, so the admin sees the blast radius before
   * typing in a new amount. Independent of the new amount itself -- see
   * repriceService.previewReprice.
   */
  async function loadRepricePreview() {
    if (!repriceEntryId) return;
    setLoadingRepricePreview(true);
    setRepriceError(null);
    setRepricePreview(null);
    setRepriceResult(null);
    try {
      const preview = await api.get(`/admin/plan-config/${repriceEntryId}/reprice-preview`);
      setRepricePreview(preview);
    } catch (err) {
      setRepriceError(err.message);
    } finally {
      setLoadingRepricePreview(false);
    }
  }

  function selectRepriceEntry(id) {
    setRepriceEntryId(id);
    setRepricePreview(null);
    setRepriceResult(null);
    setRepriceError(null);
    setRepriceNewAmount('');
  }

  async function submitReprice(e) {
    e.preventDefault();
    if (!repricePreview) return;
    const newAmountCents = dollarsToCents(repriceNewAmount);
    const confirmed = window.confirm(
      `Change "${repricePreview.displayName}" from ${formatCents(repricePreview.currentUnitAmountCents)} to ${formatCents(newAmountCents)}` +
        (repricePreview.isOneTime
          ? '? This is a one-time fee -- only future sign-ups are affected, nothing already collected changes.'
          : `? This applies to ${repricePreview.affectedSubscriptionCount} current subscription(s), effective each one's next billing cycle -- nobody is charged or credited immediately.`)
    );
    if (!confirmed) return;

    setSubmittingReprice(true);
    setRepriceError(null);
    try {
      const requestId = crypto.randomUUID();
      const result = await api.post(`/admin/plan-config/${repriceEntryId}/bulk-reprice`, { newAmountCents, requestId });
      setRepriceResult(result);
      setRepricePreview(null);
      setRepriceNewAmount('');
      load();
    } catch (err) {
      setRepriceError(err.message);
    } finally {
      setSubmittingReprice(false);
    }
  }

  async function toggleCouponActive(entry) {
    try {
      await api.patch(`/admin/coupon-config/${entry.id}`, { active: !entry.active });
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function removeCoupon(entry) {
    if (!window.confirm(`Remove "${entry.code}" from the CSR coupon list? This does not affect existing orders.`)) {
      return;
    }
    try {
      await api.delete(`/admin/coupon-config/${entry.id}`);
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function addCoupon(e) {
    e.preventDefault();
    const promo = promoCatalog.find((p) => p.id === newCoupon.stripePromotionCodeId);
    if (!promo) return;
    try {
      await api.post('/admin/coupon-config', {
        stripePromotionCodeId: promo.id,
        code: promo.code,
        displayLabel: newCoupon.displayLabel || promo.code,
        sortOrder: Number(newCoupon.sortOrder) || 0,
      });
      setNewCoupon({ stripePromotionCodeId: '', displayLabel: '', sortOrder: 0 });
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function saveConfig(e) {
    e.preventDefault();
    try {
      await api.patch('/admin/config', config);
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  const catalogNotYetAllowed = catalog.filter((p) => !planConfig.some((pc) => pc.stripe_price_id === p.id));
  const feeOptions = planConfig.filter((p) => p.category === 'fee');
  const promoCatalogNotYetAllowed = promoCatalog.filter((p) => !couponConfig.some((cc) => cc.stripe_promotion_code_id === p.id));

  return (
    <div>
      <h2>Product Configuration</h2>
      {error && <div className="error-banner">{error}</div>}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ marginTop: 0 }}>Stripe subscription sync</h3>
          <button className="btn" onClick={syncSubscriptions} disabled={syncing}>
            {syncing ? 'Syncing…' : 'Sync subscriptions from Stripe'}
          </button>
        </div>
        <p className="hint-text" style={{ marginTop: 0 }}>
          Imports every Stripe subscription into Current Plans, including ones never created through this
          tool (the old self-serve checkout, or anything added directly in Stripe) so CSRs can see and
          manage them here too. Subscriptions created going forward sync automatically — this is mainly for
          the one-time backlog, or to force a refresh if something looks out of sync. Safe to run more than
          once.
        </p>
        {syncResult && (
          <>
            <p style={{ color: syncResult.failed > 0 ? 'var(--color-danger)' : 'var(--color-success)', fontSize: 13 }}>
              Scanned {syncResult.scanned} subscription(s) in Stripe — imported {syncResult.imported} new,
              refreshed {syncResult.updated} existing
              {syncResult.failed > 0 ? `, ${syncResult.failed} failed` : ''}.
            </p>
            {syncResult.errors && syncResult.errors.length > 0 && (
              <ul style={{ fontSize: 12, color: 'var(--color-danger)', marginTop: 0 }}>
                {syncResult.errors.map((e) => (
                  <li key={e.subscriptionId}>{e.subscriptionId}: {e.message}</li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ marginTop: 0 }}>HouseCallPro customer linking</h3>
          <button className="btn" onClick={linkHcpCustomers} disabled={linkingHcp}>
            {linkingHcp ? 'Linking…' : 'Link orders to HCP customers'}
          </button>
        </div>
        <p className="hint-text" style={{ marginTop: 0 }}>
          Matches every order without an HCP link yet to its HouseCallPro customer record — the synced
          local customer list first, a live HCP search only if that finds nothing — so Current Plans and
          Order Detail can link straight to HCP. Read-only against HCP; never creates anything there. Safe
          to run more than once.
        </p>
        {hcpLinkResult && (
          <>
            <p style={{ fontSize: 13 }}>
              Scanned {hcpLinkResult.scanned} order(s) — linked {hcpLinkResult.linked}, ambiguous {hcpLinkResult.ambiguous},
              no match {hcpLinkResult.noMatch}
              {hcpLinkResult.errors > 0 ? `, ${hcpLinkResult.errors} errors` : ''}.
            </p>
            {hcpLinkResult.byMatchType && Object.keys(hcpLinkResult.byMatchType).length > 0 && (
              <p style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                Matched by: {Object.entries(hcpLinkResult.byMatchType).map(([k, v]) => `${k} (${v})`).join(', ')}
              </p>
            )}
          </>
        )}
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ marginTop: 0 }}>HouseCallPro API verification (test tool)</h3>
          <button className="btn" onClick={runHcpSmokeTest} disabled={runningHcpSmokeTest}>
            {runningHcpSmokeTest ? 'Running…' : 'Run HCP API test'}
          </button>
        </div>
        <p className="hint-text" style={{ marginTop: 0 }}>
          Calls HCP's create-customer, create-job, add-line-item, add-address, and add-note endpoints directly,
          with no Stripe subscription or real order involved — so we can confirm the field names our code
          assumes match HCP's actual API without a real signup or credit card. There is no HCP sandbox, so this
          creates real records in HouseCallPro, named "CSR-Tool Verification / SAFE-TO-DELETE" so they're easy
          to find and remove afterward. Safe to run more than once; each run creates its own new customer/job.
        </p>
        {hcpSmokeTestResult && (
          <div style={{ fontSize: 13 }}>
            {hcpSmokeTestResult.hcpCustomerUrl && (
              <p>
                Created:{' '}
                <a href={hcpSmokeTestResult.hcpCustomerUrl} target="_blank" rel="noreferrer">
                  test customer
                </a>
                {hcpSmokeTestResult.hcpJobUrl && (
                  <>
                    {' '}
                    /{' '}
                    <a href={hcpSmokeTestResult.hcpJobUrl} target="_blank" rel="noreferrer">
                      test job
                    </a>
                  </>
                )}
                {' '}— open these in HCP to check them by eye, then delete them when you're done.
              </p>
            )}
            <table>
              <thead>
                <tr><th>Step</th><th>Result</th><th>Raw response / error</th></tr>
              </thead>
              <tbody>
                {(hcpSmokeTestResult.steps || []).map((s, i) => (
                  <tr key={i}>
                    <td style={{ whiteSpace: 'nowrap' }}>{s.step}</td>
                    <td style={{ color: s.ok ? 'var(--color-success, green)' : 'var(--color-danger)' }}>
                      {s.ok ? 'OK' : 'FAILED'}
                    </td>
                    <td>
                      <details>
                        <summary style={{ cursor: 'pointer' }}>
                          {s.ok ? 'view response' : `${s.error || 'error'}${s.status ? ` (HTTP ${s.status})` : ''} — view raw`}
                        </summary>
                        <pre style={{ fontSize: 11, maxWidth: 480, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(s.ok ? s.response : { error: s.error, status: s.status, body: s.body }, null, 2)}
                        </pre>
                      </details>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ marginTop: 0 }}>CSR-sellable products</h3>
          <button className="btn" onClick={refresh} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : 'Refresh from Stripe'}
          </button>
        </div>
        {/* Horizontal scroll container: this table has grown to 9 columns
            (HCP tag and line item description were added on top of the
            original set) and no longer reliably fits a laptop-width
            screen -- scrolling the table itself, rather than letting it
            force the whole page wider, keeps the rest of the page usable. */}
        <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th style={{ minWidth: 180 }}>Name</th>
              <th style={{ minWidth: 130 }}>HCP tag(s)</th>
              <th>Category</th>
              <th>Price ID</th>
              <th style={{ minWidth: 150 }}>Line item description</th>
              <th>Featured</th><th>Required fee</th><th>Active</th><th />
            </tr>
          </thead>
          <tbody>
            {planConfig.map((p) => (
              <tr key={p.id}>
                <td>
                  <input
                    defaultValue={p.display_name}
                    onBlur={(e) => saveDisplayName(p, e.target.value)}
                    style={{ marginBottom: 0, width: '100%', minWidth: 160 }}
                  />
                </td>
                <td>
                  <input
                    defaultValue={p.service_tag || ''}
                    placeholder="none"
                    onBlur={(e) => saveServiceTag(p, e.target.value)}
                    style={{ marginBottom: 0, width: '100%', minWidth: 120 }}
                  />
                </td>
                <td>{p.category}</td>
                <td
                  style={{ fontFamily: 'monospace', fontSize: 12, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={p.stripe_price_id}
                >
                  {p.stripe_price_id}
                </td>
                <td style={{ fontSize: 12 }}>
                  {p.line_item_description ? (
                    <details>
                      <summary style={{ cursor: 'pointer' }}>
                        Cached{p.description_synced_at ? ` (as of ${new Date(p.description_synced_at).toLocaleDateString()})` : ''}
                      </summary>
                      <p style={{ maxWidth: 280, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{p.line_item_description}</p>
                      <button className="btn" style={{ fontSize: 11 }} onClick={() => fetchDescription(p)} disabled={fetchingDescriptionId === p.id}>
                        {fetchingDescriptionId === p.id ? 'Fetching…' : 'Refresh now'}
                      </button>
                    </details>
                  ) : (
                    <>
                      <span className="hint-text">none</span>{' '}
                      <button className="btn" style={{ fontSize: 11 }} onClick={() => fetchDescription(p)} disabled={fetchingDescriptionId === p.id}>
                        {fetchingDescriptionId === p.id ? 'Fetching…' : 'Fetch now'}
                      </button>
                    </>
                  )}
                </td>
                <td>
                  {p.category === 'plan' ? (
                    <input type="checkbox" style={{ width: 'auto', marginBottom: 0 }} checked={p.featured} onChange={() => toggleFeatured(p)} />
                  ) : '—'}
                </td>
                <td>
                  {p.category === 'plan' ? (
                    <select value={p.linked_fee_price_id || ''} onChange={(e) => setLinkedFee(p, e.target.value)}>
                      <option value="">None</option>
                      {feeOptions.map((f) => (
                        <option key={f.stripe_price_id} value={f.stripe_price_id}>{f.display_name}</option>
                      ))}
                    </select>
                  ) : '—'}
                </td>
                <td>{p.active ? 'Yes' : 'No'}</td>
                <td style={{ display: 'flex', gap: 8, whiteSpace: 'nowrap' }}>
                  <button className="btn" onClick={() => toggleActive(p)}>{p.active ? 'Deactivate' : 'Activate'}</button>
                  <button className="btn btn-danger" onClick={() => removeEntry(p)}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <p className="hint-text" style={{ marginTop: 8 }}>
          Featured plans show by default in the first plan on New Sign Up; unfeatured (specialized) plans only
          show once a CSR clicks "+ Add another plan". Required fee is billed automatically with that plan --
          a CSR can no longer pick or skip it, only a coupon can discount it. HCP tag(s) are added to a job's
          tags whenever that plan/add-on is on the order -- separate multiple tags with a comma (e.g.
          "Fire Ant, Rodent") to add more than one from a single row -- click into the field and edit, it saves
          when you click away. Line item description is only ever auto-cached from Stripe's product description
          the first time a plan/add-on is added below; existing rows aren't backfilled automatically, but "Fetch
          now" (or "Refresh now" once cached) pulls it from Stripe on demand, and once cached it also refreshes
          automatically (at most once a day) so edits in Stripe eventually show up here
          too.
        </p>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Bulk reprice</h3>
        <p className="hint-text" style={{ marginTop: 0 }}>
          Changes the price of a plan or add-on going forward, and rolls it out to every current subscription
          on the old price -- Stripe prices can't be edited in place, so this creates a new price behind the
          scenes and swaps everything over automatically. Takes effect on each subscription's own next
          billing cycle; nobody is charged or credited immediately. One-time fees only affect future sign-ups
          -- amounts already collected on past orders are never touched.
        </p>
        <form onSubmit={submitReprice}>
          <label>Plan or add-on</label>
          <select value={repriceEntryId} onChange={(e) => selectRepriceEntry(e.target.value)} required>
            <option value="">Select…</option>
            {planConfig.filter((p) => p.category !== 'fee').map((p) => (
              <option key={p.id} value={p.id}>
                {p.display_name} ({p.category})
              </option>
            ))}
            {planConfig.some((p) => p.category === 'fee') && (
              <optgroup label="One-time fees">
                {planConfig.filter((p) => p.category === 'fee').map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.display_name} (fee)
                  </option>
                ))}
              </optgroup>
            )}
          </select>

          {repriceEntryId && !repricePreview && (
            <button
              type="button"
              className="btn"
              style={{ marginTop: 8 }}
              onClick={loadRepricePreview}
              disabled={loadingRepricePreview}
            >
              {loadingRepricePreview ? 'Checking…' : 'Check current price & affected subscriptions'}
            </button>
          )}

          {repricePreview && (
            <>
              <p style={{ fontSize: 14, marginTop: 8 }}>
                Current price: <strong>{formatCents(repricePreview.currentUnitAmountCents)}</strong>
                {repricePreview.recurringInterval ? `/${repricePreview.recurringInterval}` : ''}
                {' — '}
                {repricePreview.isOneTime
                  ? 'one-time fee, no current subscriptions to update'
                  : `${repricePreview.affectedSubscriptionCount} current subscription(s) on this price`}
              </p>
              <label>New price ($)</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={repriceNewAmount}
                onChange={(e) => setRepriceNewAmount(e.target.value)}
                required
              />
              <button
                type="submit"
                className="btn btn-primary"
                style={{ marginTop: 8 }}
                disabled={submittingReprice || !repriceNewAmount}
              >
                {submittingReprice ? 'Applying…' : 'Confirm reprice'}
              </button>
            </>
          )}
        </form>
        {repriceError && <div className="error-banner">{repriceError}</div>}
        {repriceResult && (
          <p style={{ fontSize: 13, color: repriceResult.failed > 0 ? 'var(--color-danger)' : 'var(--color-success)' }}>
            "{repriceResult.displayName}" updated: {formatCents(repriceResult.oldUnitAmountCents)} → {formatCents(repriceResult.newUnitAmountCents)}.{' '}
            {repriceResult.isOneTime
              ? 'Applies to future sign-ups only.'
              : `${repriceResult.updated} of ${repriceResult.scanned} current subscription(s) updated, effective next billing cycle` +
                (repriceResult.failed > 0 ? `, ${repriceResult.failed} failed` : '') +
                (repriceResult.skippedNoLocalOrder > 0
                  ? ` (${repriceResult.skippedNoLocalOrder} updated in Stripe but not yet synced locally -- run "Sync subscriptions from Stripe" above)`
                  : '') +
                '.'}
          </p>
        )}
        {repriceResult && repriceResult.errors && repriceResult.errors.length > 0 && (
          <ul style={{ fontSize: 12, color: 'var(--color-danger)' }}>
            {repriceResult.errors.map((e) => (
              <li key={e.subscriptionId}>
                {e.subscriptionId}: {e.message}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Add a Stripe price to the allowlist</h3>
        <form onSubmit={addEntry}>
          <label>Stripe price</label>
          <select value={newEntry.stripePriceId} onChange={(e) => setNewEntry({ ...newEntry, stripePriceId: e.target.value })} required>
            <option value="">Select…</option>
            {catalogNotYetAllowed.map((p) => (
              <option key={p.id} value={p.id}>
                {p.productName} — {formatCents(p.unitAmountCents)}{p.recurringInterval ? `/${p.recurringInterval}` : ''} ({p.id})
              </option>
            ))}
          </select>
          <div className="form-row">
            <div>
              <label>Display name (optional override)</label>
              <input value={newEntry.displayName} onChange={(e) => setNewEntry({ ...newEntry, displayName: e.target.value })} />
            </div>
            <div>
              <label>HCP tag(s) (optional, comma-separated)</label>
              <input
                value={newEntry.serviceTag}
                placeholder="e.g. Fire Ant"
                onChange={(e) => setNewEntry({ ...newEntry, serviceTag: e.target.value })}
              />
            </div>
            <div>
              <label>Category</label>
              <select value={newEntry.category} onChange={(e) => setNewEntry({ ...newEntry, category: e.target.value })}>
                <option value="plan">Plan</option>
                <option value="addon">Add-on</option>
                <option value="fee">One-time fee</option>
              </select>
            </div>
            <div>
              <label>Sort order</label>
              <input type="number" value={newEntry.sortOrder} onChange={(e) => setNewEntry({ ...newEntry, sortOrder: e.target.value })} />
            </div>
          </div>
          {newEntry.category === 'plan' && (
            <div className="form-row">
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    style={{ width: 'auto', marginBottom: 0 }}
                    checked={newEntry.featured}
                    onChange={(e) => setNewEntry({ ...newEntry, featured: e.target.checked })}
                  />
                  Featured (shown by default)
                </label>
              </div>
              <div>
                <label>Required initial fee</label>
                <select value={newEntry.linkedFeePriceId} onChange={(e) => setNewEntry({ ...newEntry, linkedFeePriceId: e.target.value })}>
                  <option value="">None</option>
                  {feeOptions.map((f) => (
                    <option key={f.stripe_price_id} value={f.stripe_price_id}>{f.display_name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <button type="submit" className="btn btn-primary">Add to allowlist</button>
        </form>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ marginTop: 0 }}>CSR-selectable coupons</h3>
          <button className="btn" onClick={refresh} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : 'Refresh from Stripe'}
          </button>
        </div>
        <table>
          <thead>
            <tr><th>Code</th><th>Label</th><th>Active</th><th /></tr>
          </thead>
          <tbody>
            {couponConfig.map((c) => (
              <tr key={c.id}>
                <td style={{ fontFamily: 'monospace' }}>{c.code}</td>
                <td>{c.display_label}</td>
                <td>{c.active ? 'Yes' : 'No'}</td>
                <td style={{ display: 'flex', gap: 8 }}>
                  <button className="btn" onClick={() => toggleCouponActive(c)}>{c.active ? 'Deactivate' : 'Activate'}</button>
                  <button className="btn btn-danger" onClick={() => removeCoupon(c)}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Add a Stripe promotion code to the allowlist</h3>
        <form onSubmit={addCoupon}>
          <label>Stripe promotion code</label>
          <select value={newCoupon.stripePromotionCodeId} onChange={(e) => setNewCoupon({ ...newCoupon, stripePromotionCodeId: e.target.value })} required>
            <option value="">Select…</option>
            {promoCatalogNotYetAllowed.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} — {p.percentOff ? `${p.percentOff}% off` : `${formatCents(p.amountOffCents)} off`} ({p.duration})
              </option>
            ))}
          </select>
          <div className="form-row">
            <div>
              <label>Display label (optional override)</label>
              <input value={newCoupon.displayLabel} onChange={(e) => setNewCoupon({ ...newCoupon, displayLabel: e.target.value })} />
            </div>
            <div>
              <label>Sort order</label>
              <input type="number" value={newCoupon.sortOrder} onChange={(e) => setNewCoupon({ ...newCoupon, sortOrder: e.target.value })} />
            </div>
          </div>
          <button type="submit" className="btn btn-primary">Add to allowlist</button>
        </form>
      </div>

      {config && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Thresholds</h3>
          <form onSubmit={saveConfig}>
            <label>Refund approval threshold ($)</label>
            <input
              type="number"
              step="0.01"
              value={(config.refundApprovalThresholdCents / 100).toFixed(2)}
              onChange={(e) => setConfig({ ...config, refundApprovalThresholdCents: dollarsToCents(e.target.value) })}
            />
            <label>Commission retention gate (days)</label>
            <input
              type="number"
              value={config.commissionRetentionGateDays}
              onChange={(e) => setConfig({ ...config, commissionRetentionGateDays: Number(e.target.value) })}
            />
            <button type="submit" className="btn btn-primary">Save</button>
          </form>
        </div>
      )}
    </div>
  );
}
